import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Stack,
  Radio,
  TextInput,
  Group,
  Text,
  Textarea,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { DateOrAgeSelector } from '@/components/inputs/DateOrAgeSelector';
import type {
  PhysicalGrowthData,
  PhysicalGrowthValue,
} from '@/components/inputs/PhysicalGrowthInput';

export interface PhysicalGrowthInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Callback to save measurement. Returns the saved data. */
  onSave: (data: PhysicalGrowthData) => Promise<void>;
  /** Callback called after successful save. */
  onSaveSuccess?: (data: PhysicalGrowthData) => Promise<void>;
  data?: PhysicalGrowthData;
  mode: 'add' | 'edit';
}

const createEmptyData = (): PhysicalGrowthData => ({
  value: null,
  comments: '',
  height: '',
  weight: '',
  headCircumference: '',
  measurementAge: {
    type: 'today',
  },
});

export const PhysicalGrowthInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  data,
  mode,
}: PhysicalGrowthInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state
  const [formData, setFormData] =
    useState<PhysicalGrowthData>(createEmptyData());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PhysicalGrowthData | null>(null);

  // Reset form when modal opens or data changes
  useEffect(() => {
    if (opened) {
      setInputKey((prev) => prev + 1);
      const initialData = data ? { ...data } : createEmptyData();
      setFormData(initialData);
      // Capture baseline immediately after setting state
      baselineRef.current = { ...initialData };
    }
  }, [opened, data]);

  // Update data
  const updateData = <K extends keyof PhysicalGrowthData>(
    field: K,
    value: PhysicalGrowthData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formData, baselineRef.current);
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    setInputKey((prev) => prev + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(formData);
      // Notify parent component of successful save and wait for reload
      if (onSaveSuccess) {
        await onSaveSuccess(formData);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save physical growth:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to save physical growth'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('physicalGrowth.addMeasurement', {
              ns: 'patient-pages',
              defaultValue: 'Add Physical Growth Measurement',
            })
          : t('physicalGrowth.editMeasurement', {
              ns: 'patient-pages',
              defaultValue: 'Edit Physical Growth Measurement',
            })
      }
      size="lg"
    >
      <Stack gap="md" key={inputKey}>
        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        {/* Status and Comments */}
        <Group>
          <Radio.Group
            label={t('physicalGrowth.title', {
              ns: 'patient-pages',
              defaultValue: 'Physical Growth',
            })}
            value={formData.value || ''}
            onChange={(value) =>
              updateData('value', (value as PhysicalGrowthValue) || null)
            }
          >
            <Stack mt="xs" gap="sm">
              <Radio
                value="within-normal-limits"
                label={t('physicalGrowth.withinNormalLimits', {
                  ns: 'patient-pages',
                  defaultValue: 'Within normal limits',
                })}
              />
              <Radio
                value="pathological"
                label={t('physicalGrowth.pathological', {
                  ns: 'patient-pages',
                  defaultValue: 'Pathological',
                })}
              />
            </Stack>
          </Radio.Group>

          <Textarea
            label={t('physicalGrowth.comments', {
              ns: 'patient-pages',
              defaultValue: 'Comments',
            })}
            placeholder={t('physicalGrowth.commentsPlaceholder', {
              ns: 'patient-pages',
              defaultValue: 'Enter comments...',
            })}
            value={formData.comments}
            onChange={(e) => updateData('comments', e.currentTarget.value)}
            flex={1}
          />
        </Group>

        {/* Measurements */}
        <Group grow wrap="nowrap">
          <TextInput
            name="weight"
            type="number"
            min="0"
            step="0.01"
            label={t('examination.content.vitals.weight', {
              ns: 'patient-pages',
              defaultValue: 'Weight',
            })}
            value={formData.weight}
            onChange={(e) => updateData('weight', e.currentTarget.value)}
            rightSection={
              <Text size="sm" c="dimmed">
                kg
              </Text>
            }
          />
          <TextInput
            name="height"
            type="number"
            min="0"
            step="0.1"
            label={t('examination.content.vitals.height', {
              ns: 'patient-pages',
              defaultValue: 'Height',
            })}
            value={formData.height}
            onChange={(e) => updateData('height', e.currentTarget.value)}
            rightSection={
              <Text size="sm" c="dimmed">
                cm
              </Text>
            }
          />
          <TextInput
            name="headCircumference"
            type="number"
            min="0"
            step="0.1"
            label={t('examination.content.vitals.headCircumference', {
              ns: 'patient-pages',
              defaultValue: 'Head Circumference',
            })}
            value={formData.headCircumference}
            onChange={(e) =>
              updateData('headCircumference', e.currentTarget.value)
            }
            rightSection={
              <Text size="sm" c="dimmed">
                cm
              </Text>
            }
          />
        </Group>

        {/* Measurement Age */}
        <DateOrAgeSelector
          value={formData.measurementAge || { type: 'today' }}
          onChange={(measurementAge) =>
            updateData('measurementAge', measurementAge)
          }
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('physicalGrowth.addButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Add',
                })
              : t('physicalGrowth.saveButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
